import React, { useState, useEffect } from "react";
import axios from "axios";
import { Printer, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const esc = (v) => String(v ?? "-").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Satu builder HTML untuk preview & cetak -> dijamin sinkron
const buildInvoiceHtml = (invoice, comp, s) => {
  const ppnPct = Number(s.invoice_tax_percent || 0);
  const taxEnabled = !!s.invoice_tax_enabled && ppnPct > 0;
  const nominal = Number(invoice.nominal || 0);
  const diskon = Number(invoice.diskon || 0);
  const total = Number(invoice.total_tagihan || 0);
  let ppnRow = "";
  if (taxEnabled) {
    if (s.invoice_tax_inclusive) {
      const dpp = Math.round(total / (1 + ppnPct / 100));
      ppnRow = `<div class="row"><span>PPN ${ppnPct}% (sudah termasuk):</span><span>${rp(total - dpp)}</span></div>
                <div class="row"><span>DPP (Dasar Pengenaan Pajak):</span><span>${rp(dpp)}</span></div>`;
    } else {
      const ppn = Number(invoice.pajak_ppn || 0) || Math.round((nominal - diskon) * ppnPct / 100);
      ppnRow = `<div class="row"><span>PPN ${ppnPct}%:</span><span>${rp(ppn)}</span></div>`;
    }
  }

  const banks = Array.isArray(s.invoice_payment_methods) ? s.invoice_payment_methods : [];
  const payHtml = banks.length
    ? banks.map((b) => `<p class="pay-line">&bull; <strong>${esc(b.bank)}</strong>: ${esc(b.no_rekening)} a/n ${esc(b.atas_nama)}</p>`).join("")
    : '<p class="pay-line muted">Silakan hubungi admin/billing untuk metode pembayaran resmi.</p>';

  const notes = Array.isArray(s.invoice_notes) ? s.invoice_notes.filter((n) => String(n || "").trim()) : [];
  const notesHtml = notes.length
    ? `<div class="notes"><p class="notes-title">Catatan:</p><ul>${notes.map((n) => `<li>${esc(n)}</li>`).join("")}</ul></div>`
    : "";

  const signerName = s.invoice_signer_name || "____________________";
  const signerTitle = s.invoice_signer_title || "Finance Dept.";
  const noInv = esc(invoice.no_invoice);

  const sigType = s.invoice_signature_type === "manual" ? "manual" : "barcode";
  let signVisual = "";
  if (sigType === "manual") {
    const stampHtml = s.invoice_stamp_image ? `<img class="stamp-img" src="${esc(s.invoice_stamp_image)}" alt="stempel" onerror="this.style.display='none'" />` : "";
    const sigHtml = s.invoice_signature_image ? `<img class="sig-img" src="${esc(s.invoice_signature_image)}" alt="tanda tangan" onerror="this.style.display='none'" />` : "";
    signVisual = (stampHtml || sigHtml) ? `<div class="sig-wrap">${stampHtml}${sigHtml}</div>` : "";
  } else if (s.invoice_barcode_enabled !== false) {
    signVisual = s.invoice_barcode_type === "barcode"
      ? `<div class="barcode-bars">${Array.from({ length: 30 }).map((_, i) => `<span style="width:${i % 3 ? 1 : 2}px"></span>`).join("")}</div><p class="barcode-text">${noInv}</p>`
      : `<img class="qr-img" src="https://api.qrserver.com/v1/create-qr-code/?size=90x90&data=${encodeURIComponent(invoice.no_invoice || "")}" alt="QR ${noInv}" onerror="this.style.display='none'" /><p class="barcode-text">${noInv}</p>`;
  }

  const identityBlock = (o, side) => `
    <div class="id-block">
      ${o.label ? `<p class="supported-by">${esc(o.label)}</p>` : ""}
      <div class="company${side === "right" ? " co-right" : ""}">
        ${o.logo ? `<img src="${esc(o.logo)}" alt="logo" class="logo" onerror="this.style.display='none'" />` : ""}
        <div>
          <h1>${esc(o.nama || "ISP")}</h1>
          ${o.badan ? `<p class="badan">${esc(o.badan)}</p>` : ""}
          ${o.alamat ? `<p class="muted small">${esc(o.alamat)}</p>` : ""}
          <p class="muted small">Telp: ${esc(o.telepon || "-")}${o.email ? ` &bull; ${esc(o.email)}` : ""}${o.website ? ` &bull; ${esc(o.website)}` : ""}</p>
        </div>
      </div>
    </div>`;
  const showIsp = s.invoice_show_isp !== false;
  const showReseller = !!s.invoice_show_reseller;
  const ispPos = s.invoice_isp_position === "right" ? "right" : "left";
  const resellerPos = s.invoice_reseller_position === "left" ? "left" : "right";
  const leftBlocks = [];
  const rightBlocks = [];
  if (showIsp) (ispPos === "left" ? leftBlocks : rightBlocks).push(identityBlock({ label: (showReseller && comp.reseller_logo) ? "Didukung oleh :" : "", logo: comp.logo || s.app_logo, nama: comp.nama_isp || s.app_name, badan: comp.badan_usaha, alamat: comp.alamat, telepon: comp.telepon, email: comp.email, website: comp.website }, ispPos));
  if (showReseller) (resellerPos === "left" ? leftBlocks : rightBlocks).push(identityBlock({ logo: comp.reseller_logo, nama: comp.reseller_nama, badan: comp.reseller_badan_usaha, alamat: comp.reseller_alamat, telepon: comp.reseller_telepon, email: comp.reseller_email, website: comp.reseller_website }, resellerPos));
  const statusClass = invoice.status === "PAID" ? "paid" : "unpaid";
  const statusLabel = invoice.status === "PAID" ? "LUNAS (PAID)" : "BELUM BAYAR (UNPAID)";
  const paidRow = invoice.tanggal_bayar ? `<div><span class="muted">Dibayar Pada: </span><span class="paid-text">${esc(invoice.tanggal_bayar)}</span></div>` : "";

  return `
  <div class="sheet">
    <div class="header">
      <div class="hdr-col hdr-left">${leftBlocks.join("")}</div>
      <div class="hdr-col hdr-right">${rightBlocks.join("")}</div>
    </div>

    <div class="inv-meta">
      <span class="inv-badge">INVOICE</span>
      <span class="inv-no">${noInv}</span>
      <span class="status ${statusClass}">${statusLabel}</span>
    </div>

    <div class="info-grid">
      <div>
        <span class="lbl">Tagihan Kepada:</span>
        <p class="strong">${esc(invoice.pelanggan_nama)}</p>
        <p class="muted">${esc(invoice.pelanggan_no_hp)}</p>
        <p class="muted">${esc(invoice.pelanggan_alamat)}</p>
      </div>
      <div class="right">
        <div><span class="muted">Periode Tagihan: </span><span class="strong">${esc(invoice.periode_bulan)}</span></div>
        <div><span class="muted">Tanggal Terbit: </span><span>${esc(invoice.tanggal_terbit)}</span></div>
        <div><span class="muted">Jatuh Tempo: </span><span class="due">${esc(invoice.jatuh_tempo)}</span></div>
        ${paidRow}
      </div>
    </div>

    <table>
      <thead><tr><th>Deskripsi Layanan</th><th class="ctr">Periode</th><th class="num">Jumlah (IDR)</th></tr></thead>
      <tbody><tr>
        <td><p class="strong">Langganan Internet: ${esc(invoice.paket_nama)}</p><p class="muted small">Layanan internet fiber optic dedicated &amp; unlimited</p></td>
        <td class="ctr muted">${esc(invoice.periode_bulan)}</td>
        <td class="num strong">${rp(nominal)}</td>
      </tr></tbody>
    </table>

    <div class="totals">
      <div class="row"><span>Subtotal:</span><span>${rp(nominal)}</span></div>
      ${diskon ? `<div class="row"><span>Diskon Promo:</span><span>-${rp(diskon)}</span></div>` : ""}
      ${ppnRow}
      <div class="row grand"><span>TOTAL TAGIHAN:</span><span>${rp(total)}</span></div>
    </div>

    ${notesHtml}

    <div class="footer-grid">
      <div>
        <p class="strong small-h">Metode Pembayaran Resmi:</p>
        ${payHtml}
        <p class="muted italic small">Harap sertakan no. invoice saat melakukan transfer manual.</p>
      </div>
      <div class="sign">
        ${signVisual}
        <p class="sign-name">${esc(signerName)}</p>
        <p class="muted small">${esc(signerTitle)}</p>
      </div>
    </div>
  </div>`;
};

const PRINT_CSS = `
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #0f172a; background: #fff; }
  .sheet { width: 210mm; min-height: 297mm; padding: 15mm 16mm; margin: 0 auto; display: flex; flex-direction: column; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; border-bottom: 2.5px solid #0f172a; padding-bottom: 14px; }
  .hdr-col { display: flex; flex-direction: column; gap: 12px; }
  .hdr-right { align-items: flex-end; text-align: right; }
  .company { display: flex; gap: 12px; align-items: flex-start; }
  .company.co-right { flex-direction: row-reverse; text-align: right; }
  .supported-by { font-size: 9px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px; }
  .logo { width: 58px; height: 58px; object-fit: contain; border-radius: 8px; }
  .company h1 { font-size: 19px; font-weight: 900; color: #155e75; letter-spacing: 1px; margin: 0; }
  .badan { font-size: 11px; font-weight: 700; color: #475569; margin: 2px 0; }
  .muted { color: #64748b; } .small { font-size: 10px; margin: 1px 0; }
  .inv-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin: 14px 0 4px; }
  .inv-badge { display: inline-block; background: #0f172a; color: #fff; font-family: monospace; font-weight: 700; font-size: 12px; padding: 4px 12px; border-radius: 4px; }
  .inv-no { font-size: 14px; font-weight: 700; font-family: monospace; color: #1e293b; margin: 0; }
  .status { display: inline-block; padding: 3px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; border-radius: 4px; }
  .status.paid { background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; }
  .status.unpaid { background: #ffe4e6; color: #9f1239; border: 1px solid #fda4af; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 16px 0; padding: 10px 0; border-bottom: 1px solid #e2e8f0; font-size: 11px; }
  .info-grid p { margin: 2px 0; } .info-grid .right { text-align: right; line-height: 1.7; }
  .lbl { font-size: 10px; color: #94a3b8; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; display: block; margin-bottom: 4px; }
  .strong { font-weight: 700; color: #0f172a; margin: 2px 0; } .due { font-weight: 700; color: #e11d48; } .paid-text { font-weight: 600; color: #047857; }
  table { width: 100%; border-collapse: collapse; margin: 14px 0; }
  th { background: #f1f5f9; color: #334155; font-size: 11px; text-align: left; padding: 8px; border-bottom: 1px solid #cbd5e1; }
  td { padding: 10px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  .ctr { text-align: center; } .num { text-align: right; }
  .totals { margin-left: auto; width: 260px; font-size: 12px; }
  .totals .row { display: flex; justify-content: space-between; padding: 3px 0; color: #475569; }
  .totals .grand { font-weight: 900; font-size: 14px; color: #0f172a; border-top: 2px solid #0f172a; margin-top: 6px; padding-top: 8px; }
  .footer-grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 16px; margin-top: auto; padding-top: 18px; border-top: 1px solid #e2e8f0; font-size: 10px; }
  .small-h { font-size: 11px; margin-bottom: 4px; } .pay-line { color: #475569; margin: 2px 0; } .italic { font-style: italic; }
  .sign { text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; }
  .qr-img { width: 84px; height: 84px; border: 1px solid #cbd5e1; border-radius: 4px; }
  .barcode-bars { display: flex; align-items: flex-end; gap: 2px; height: 48px; }
  .barcode-bars span { display: inline-block; background: #0f172a; height: 100%; }
  .barcode-text { font-family: monospace; font-size: 9px; color: #334155; margin: 4px 0 0; }
  .sig-wrap { position: relative; display: inline-flex; align-items: center; justify-content: center; min-height: 62px; padding: 4px 0; }
  .sig-img { max-height: 62px; max-width: 170px; object-fit: contain; position: relative; z-index: 2; }
  .stamp-img { max-height: 84px; max-width: 130px; object-fit: contain; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.85; z-index: 1; }
  .sign-name { font-weight: 700; color: #0f172a; margin: 10px 0 0; border-top: 1px solid #94a3b8; padding-top: 4px; min-width: 140px; }
  .notes { clear: both; margin-top: 16px; padding: 10px 12px; border: 1px solid #e2e8f0; border-left: 3px solid #0f172a; border-radius: 4px; background: #f8fafc; }
  .notes-title { font-size: 11px; font-weight: 700; color: #0f172a; margin: 0 0 4px; }
  .notes ul { margin: 0; padding-left: 16px; }
  .notes li { font-size: 10px; color: #475569; margin: 2px 0; line-height: 1.5; }
`;

export const InvoicePrintModal = ({ invoice, company, onClose }) => {
  const [settings, setSettings] = useState(null);
  const [comp, setComp] = useState(company || null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [appRes, compRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/pengaturan/aplikasi`, { withCredentials: true }),
          company
            ? Promise.resolve({ data: company })
            : axios.get(`${BACKEND_URL}/api/pengaturan/perusahaan`, { withCredentials: true }),
        ]);
        if (!mounted) return;
        setSettings(appRes.data || {});
        setComp(compRes.data || {});
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (!invoice) return null;

  const ready = settings && comp;
  const invoiceHtml = ready ? buildInvoiceHtml(invoice, comp, settings) : "";

  const handlePrint = () => {
    if (!ready) return;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>Invoice ${invoice.no_invoice}</title><style>${PRINT_CSS}</style></head><body>${invoiceHtml}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800 bg-[#090f1d]">
          <div className="flex items-center gap-2">
            <Printer className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-bold text-white">Invoice Cetak Standar / PDF (A4 Full Page)</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-md" data-testid="close-invoice-print">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto bg-slate-950/80 flex justify-center">
          {ready ? (
            <div
              data-testid="invoice-a4-preview"
              className="bg-white rounded-lg shadow-2xl border border-slate-200 w-full max-w-[720px] overflow-hidden"
            >
              <style>{PRINT_CSS}</style>
              <div dangerouslySetInnerHTML={{ __html: invoiceHtml }} style={{ zoom: 0.62 }} />
            </div>
          ) : (
            <p className="text-xs text-slate-400 py-10">Memuat pengaturan invoice &amp; perusahaan...</p>
          )}
        </div>

        <div className="p-4 bg-[#0d1527] border-t border-slate-800 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-lg transition-colors">
            Tutup
          </button>
          <button
            onClick={handlePrint}
            disabled={!ready}
            data-testid="print-invoice-btn"
            className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-lg shadow-cyan-500/25 transition-all disabled:opacity-50"
          >
            <Printer className="w-4 h-4" />
            <span>Cetak Invoice / Unduh PDF</span>
          </button>
        </div>
      </div>
    </div>
  );
};
